import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS } from '../../constants/colors'
import { DefaultModal } from '../../shared/components/DefaultModal'
import { Button } from '../../interaxDesignSystemComponents'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { PrivacyNoticeContent } from './PrivacyNoticeContent'
import {
  PRIVACY_NOTICE_MODAL_MAX_WIDTH,
  modalBodySx,
  modalFooterButtonSx,
  modalFooterSx,
  modalHeaderSx,
  modalLayoutSx,
  modalTitleSx,
  modalWordmarkSx,
  progressFillSx,
  progressTrackSx
} from './privacyNoticeStyles'

const WORDMARK_SRC = '/assets/img/IMG_Logo.png'
const TITLE_ID = 'privacy-notice-title'
const FULLY_READ_PERCENT = 100

interface PrivacyNoticeModalProps {
  isOpen: boolean
  onClose: () => void
  onAcknowledge: () => void
}

export function PrivacyNoticeModal({ isOpen, onClose, onAcknowledge }: PrivacyNoticeModalProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)

  const handleScroll = useCallback(() => {
    const element = scrollRef.current
    if (!element) return
    const scrollableHeight = element.scrollHeight - element.clientHeight
    const progress =
      scrollableHeight > 0 ? (element.scrollTop / scrollableHeight) * FULLY_READ_PERCENT : FULLY_READ_PERCENT
    setScrollProgress(Math.round(progress))
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setScrollProgress(0)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [isOpen])

  const handleAcknowledge = () => {
    onAcknowledge()
    onClose()
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={PRIVACY_NOTICE_MODAL_MAX_WIDTH}
      backgroundColor={INTERAX_COLORS.offBlack}
    >
      <Box role="dialog" aria-modal="true" aria-labelledby={TITLE_ID} sx={modalLayoutSx}>
        <Box sx={modalHeaderSx}>
          <Box component="img" src={WORDMARK_SRC} alt={t('privacyNotice.logoAlt')} sx={modalWordmarkSx} />
          <Typography id={TITLE_ID} component="h2" sx={modalTitleSx}>
            {t('privacyNotice.title')}
          </Typography>
        </Box>
        <Box aria-hidden="true" sx={progressTrackSx}>
          <Box sx={progressFillSx(scrollProgress)} />
        </Box>

        <Box ref={scrollRef} onScroll={handleScroll} sx={modalBodySx}>
          <PrivacyNoticeContent />
        </Box>

        <Box sx={modalFooterSx}>
          <Box sx={modalFooterButtonSx}>
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t('common.close')}
            </Button>
          </Box>
          <Box sx={modalFooterButtonSx}>
            <Button variant="primary" fullWidth onClick={handleAcknowledge}>
              {t('privacyNotice.acknowledge')}
            </Button>
          </Box>
        </Box>
      </Box>
    </DefaultModal>
  )
}
